import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import Layout from '../components/layout/Layout';
import { AnimatedSection, StaggerContainer, StaggerItem } from '../components/common/AnimatedSection';
import { theme, media, onDark } from '../styles/theme';
import { brand } from '../styles/brand';
import {
  PageContainer,
  Section,
  Card,
  SubTitle,
  Text,
} from '../styles/components';
import FeaturedCards from '../components/buylist/FeaturedCards';
import blogPosts from '../data/blogPosts';

const HeroSection = styled.section`
  background-color: ${theme.colors.secondary};
  color: ${theme.colors.white};
  text-align: center;
`;

const HeroContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 5rem 2rem 5rem;

  ${media.tablet} {
    padding: 3rem 1.5rem 3rem;
  }

  ${media.mobile} {
    padding: 2.5rem 1rem 2.5rem;
  }
`;

const HeroBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  background-color: ${theme.colors.accent};
  color: ${theme.colors.white};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.fontSizes.xs};
  font-weight: ${theme.fontWeights.semibold};
  margin-bottom: ${theme.spacing.md};
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

const HeroTitle = styled.h1`
  font-size: 3.75rem;
  margin-bottom: ${theme.spacing.sm};
  font-weight: ${theme.fontWeights.medium};
  letter-spacing: ${brand.typography.headingLetterSpacing};
  line-height: 1.1;

  ${media.tablet} {
    font-size: ${theme.fontSizes.xxxl};
  }

  ${media.mobile} {
    font-size: ${theme.fontSizes.xxl};
  }
`;

// Keyword part of the H1, shown as a smaller second line under the brand name
const HeroTitleTagline = styled.span`
  display: block;
  margin-top: ${theme.spacing.xs};
  font-size: ${theme.fontSizes.xl};
  font-weight: ${theme.fontWeights.medium};
  letter-spacing: normal;
  opacity: 0.9;

  ${media.mobile} {
    font-size: ${theme.fontSizes.lg};
  }
`;

const HeroSubtitle = styled.p`
  font-size: ${theme.fontSizes.lg};
  margin-bottom: ${theme.spacing.lg};
  opacity: 0.8;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const HeroButtons = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  justify-content: center;
  flex-wrap: wrap;
`;

// The hero sits on the dark chrome, so the button inverts the way the webshop's does.
const HeroPrimaryButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  padding: 0.75rem 2rem;
  background-color: ${onDark.button};
  color: ${onDark.buttonText};
  text-decoration: none;
  border-radius: ${theme.borderRadius.md};
  font-weight: ${theme.fontWeights.medium};
  font-size: ${theme.fontSizes.md};
  transition: all ${theme.transitions.fast};

  &:hover {
    box-shadow: 0 0 0 1.3px ${onDark.buttonText}, 0 0 0 2.3px ${onDark.button};
  }
`;

const HeroSecondaryButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  padding: 0.75rem 2rem;
  background-color: transparent;
  color: ${theme.colors.white};
  text-decoration: none;
  border-radius: ${theme.borderRadius.md};
  font-weight: ${theme.fontWeights.medium};
  font-size: ${theme.fontSizes.md};
  border: 1px solid rgba(255, 255, 255, 0.3);
  transition: all ${theme.transitions.fast};

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    transform: translateY(-1px);
  }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${theme.spacing.md};

  ${media.tablet} {
    grid-template-columns: repeat(2, 1fr);
  }

  ${media.mobile} {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled(Card)`
  text-align: center;
  padding: ${theme.spacing.lg};
  text-decoration: none;
  color: ${theme.colors.white};
  background-color: ${theme.colors.secondary};
  display: block;
  height: 100%;

  h3 {
    color: ${theme.colors.white};
  }

  p {
    color: ${theme.colors.white};
    opacity: 0.8;
  }
`;

const FeatureIcon = styled.div`
  font-size: ${theme.fontSizes.xxxl};
  margin-bottom: ${theme.spacing.sm};
`;

const BlogSection = styled.section`
  background-color: ${theme.colors.background};
  padding: ${theme.spacing.xl} 0;
`;

const BlogSectionInner = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;

  ${media.mobile} {
    padding: 0 1rem;
  }
`;

const BlogSectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: ${theme.spacing.md};
`;

const BlogSectionTitle = styled.h2`
  font-size: ${theme.fontSizes.xl};
  font-weight: ${theme.fontWeights.medium};
  color: ${theme.colors.text};
`;

const BlogSectionLink = styled(Link)`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.primary};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const LatestPostCard = styled(Link)`
  display: block;
  max-width: 720px;
  margin: 0 auto;
  background-color: ${theme.colors.white};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.lg};
  text-decoration: none;
  color: inherit;
  box-shadow: ${theme.shadows.card};
  transition: box-shadow ${theme.transitions.fast}, transform ${theme.transitions.fast};

  &:hover {
    box-shadow: ${theme.shadows.cardHover};
    transform: translateY(-2px);
  }
`;

const LatestPostDate = styled.span`
  display: block;
  font-size: ${theme.fontSizes.xs};
  color: ${theme.colors.lightText};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: ${theme.spacing.xs};
`;

const LatestPostTitle = styled.h3`
  font-size: ${theme.fontSizes.xl};
  font-weight: ${theme.fontWeights.medium};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.xs};
  line-height: 1.3;
`;

const LatestPostExcerpt = styled.p`
  font-size: ${theme.fontSizes.md};
  color: ${theme.colors.lightText};
  line-height: 1.6;
  margin-bottom: ${theme.spacing.sm};
`;

const LatestPostReadMore = styled.span`
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.medium};
  color: ${theme.colors.primary};
`;

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('nb-NO', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

const HomePage: React.FC = () => {
  const latestPost = blogPosts[0];

  return (
    <Layout>
      <Helmet>
        <title>MTG Buylist i Norge – selg Magic-kort til Tryllepapp</title>
        <meta name="description" content="Tryllepapp er en stående MTG buylist i Norge. Søk opp Magic: The Gathering-kortene dine, se prisen med en gang, og få rask utbetaling eller 10% ekstra i butikkreditt." />
        <link rel="canonical" href="https://selg.tryllepapp.no/" />
        <meta property="og:title" content="MTG Buylist i Norge – selg Magic-kort til Tryllepapp" />
        <meta property="og:description" content="Stående MTG buylist i Norge. Se prisen på kortene dine med en gang, og få rask utbetaling eller 10% ekstra i butikkreditt." />
        <meta property="og:url" content="https://selg.tryllepapp.no/" />
      </Helmet>
      <AnimatedSection>
        <HeroSection>
          <HeroContent>
            <HeroBadge>Rask utbetaling · 10% ekstra i butikkreditt</HeroBadge>
            <HeroTitle>
              Tryllepapp
              <HeroTitleTagline>MTG buylist i Norge</HeroTitleTagline>
            </HeroTitle>
            <HeroSubtitle>
              Selg eller bytt inn Magic: The Gathering-kort på den enkleste måten i Norge.
            </HeroSubtitle>
            <HeroButtons>
              <HeroPrimaryButton to="/buylist">Selg kort til oss</HeroPrimaryButton>
              <HeroSecondaryButton to="/selge-samling">Selg kort eller samling</HeroSecondaryButton>
            </HeroButtons>
          </HeroContent>
        </HeroSection>
      </AnimatedSection>

      <PageContainer>
        <Section style={{ marginBottom: 0 }}>
          <StaggerContainer>
            <FeatureGrid>
              <StaggerItem>
                <FeatureCard as="a" href="https://tryllepapp.no" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                  <FeatureIcon>🛒</FeatureIcon>
                  <SubTitle>Kjøp kort</SubTitle>
                  <Text>
                    Kjøp enkeltkort fra vårt utvalg av singles i nettbutikken vår!
                  </Text>
                </FeatureCard>
              </StaggerItem>

              <StaggerItem>
                <FeatureCard as={Link} to="/buylist" style={{ textDecoration: 'none' }}>
                  <FeatureIcon>💰</FeatureIcon>
                  <SubTitle>Selge kort</SubTitle>
                  <Text>
                    Selg dine singles til oss og få rask utbetaling til konto. Vi kan også tilby 10% mer i butikkreditt!
                  </Text>
                </FeatureCard>
              </StaggerItem>

              <StaggerItem>
                <FeatureCard as={Link} to="/selge-samling" style={{ textDecoration: 'none' }}>
                  <FeatureIcon>📦</FeatureIcon>
                  <SubTitle>Selge MTG-kort og samlinger</SubTitle>
                  <Text>
                    Vi kjøper Magic-kort og hele samlinger. Vi henter store samlinger hvor som helst i Norge!
                  </Text>
                </FeatureCard>
              </StaggerItem>

              <StaggerItem>
                <FeatureCard as={Link} to="/csv-opplasting" style={{ textDecoration: 'none' }}>
                  <FeatureIcon>📋</FeatureIcon>
                  <SubTitle>Last opp kortliste</SubTitle>
                  <Text>
                    Har du en liste med kort? Last opp en CSV-fil og legg til kort i handlekurven raskt og enkelt!
                  </Text>
                </FeatureCard>
              </StaggerItem>

            </FeatureGrid>
          </StaggerContainer>
        </Section>
      </PageContainer>

      <FeaturedCards />

      {latestPost && (
        <BlogSection>
          <BlogSectionInner>
            <BlogSectionHeader>
              <BlogSectionTitle>Siste fra bloggen</BlogSectionTitle>
              <BlogSectionLink to="/blogg">Se alle innlegg →</BlogSectionLink>
            </BlogSectionHeader>
            <LatestPostCard to={`/blogg/${latestPost.slug}`}>
              <LatestPostDate>{formatDate(latestPost.date)}</LatestPostDate>
              <LatestPostTitle>{latestPost.title}</LatestPostTitle>
              <LatestPostExcerpt>{latestPost.excerpt}</LatestPostExcerpt>
              <LatestPostReadMore>Les innlegget →</LatestPostReadMore>
            </LatestPostCard>
          </BlogSectionInner>
        </BlogSection>
      )}
    </Layout>
  );
};

export default HomePage;
